// @flow

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import { GSGrid, Checkbox, Icons, Button } from "@components";
import { formatDate } from "@transformers/date";
import type { AssignedShiftPattern } from "../../reducer/hierarchy/types";

type OwnProps = $Exact<{
  shiftPatterns: AssignedShiftPattern[],
  deleteHierarchyShiftPatternAssignment: (
    data: Array<
      $Exact<{
        assignmentRuleId: number,
        hierarchyUnassignShiftPatternDTO: $Exact<{
          isCascading: boolean,
        }>,
      }>,
    >,
  ) => void,
  toggleModal: (
    $Exact<{
      visible: boolean,
      assignedShiftPattern?: ?AssignedShiftPattern,
    }>,
  ) => () => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  selected: number[],
}>;

export class ShiftPatternsTable extends React.Component<Props, ComponentState> {
  state = {
    selected: [],
  };

  getDepartmentScope = (data: AssignedShiftPattern) => {
    if (!data.isInherited && !data.isCascading) {
      return this.props.t("THIS_DEPARTMENT_ONLY");
    }

    if (data.isCascading && !data.isInherited) {
      return this.props.t("THIS_DEPARTMENT_AND_CHILDREN");
    }

    if (data.isInherited) {
      `${this.props.t("INHERITED_FROM")} ${data.inheritedFromOrganisationalUnit.name}`;
    }

    return "";
  };

  get allowedDataItems() {
    return this.props.shiftPatterns.reduce(
      (acc, current) => (current.isInherited ? [...acc] : [...acc, current.id]),
      [],
    );
  }

  deselectAllHandler = () =>
    this.setState({
      selected: [],
    });

  get columns() {
    return [
      {
        key: "id",
        name: "ID",
        width: 80,
      },
      {
        key: "shiftPattern",
        name: this.props.t("SET_SHIFT_PATTERN"),
        align: "left",
        renderCell: ({ row }: { row: AssignedShiftPattern }) => row.shiftPattern.name,
      },
      {
        key: "departmentScope",
        name: this.props.t("DEPARTMENT_SCOPE"),
        align: "left",
        renderCell: ({ row }: { row: AssignedShiftPattern }) => this.getDepartmentScope(row),
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_ADDED"),
        renderCell: ({ row }: { row: AssignedShiftPattern }) => formatDate(row.createdAt),
      },
      {
        key: "createdBy",
        name: this.props.t("ADDED_BY"),
        align: "left",
        renderCell: ({ row }: { row: AssignedShiftPattern }) =>
          row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : "",
      },
      {
        key: "edit",
        align: "center",
        width: 50,
        name: this.props.t("EDIT"),
        renderCell: ({ row }: { row: AssignedShiftPattern }) =>
          !row.isInherited && (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.props.toggleModal({ visible: true, assignedShiftPattern: row })}
            >
              edit
            </span>
          ),
      },
      {
        key: "delete",
        align: "center",
        width: 50,
        name: this.props.t("DELETE"),
        renderCell: ({ row }: { row: AssignedShiftPattern }) =>
          !row.isInherited && (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.onDeleteHierarchyShiftPatternAssignment([row.id])}
            >
              delete
            </span>
          ),
      },
    ];
  }

  onDeleteHierarchyShiftPatternAssignment = (shiftPatternIds: number[]) => () => {
    const data = shiftPatternIds.reduce((acc, current) => {
      const element: ?AssignedShiftPattern = this.props.shiftPatterns.find(
        item => item.id === current,
      );
      return element
        ? [
            ...acc,
            {
              assignmentRuleId: element.id,
              hierarchyUnassignShiftPatternDTO: {
                isCascading: element.isCascading,
              },
            },
          ]
        : [...acc];
    }, []);
    this.props.deleteHierarchyShiftPatternAssignment(data);
    this.deselectAllHandler();
  };

  handleOnSelectedRowsChange = (selected: number[]) => {
    this.setState({
      selected,
    });
  };

  render() {
    return (
      <Fragment>
        <GSGrid
          rows={this.props.shiftPatterns}
          selectedRows={this.state.selected}
          onSelectedRowsChange={this.handleOnSelectedRowsChange}
          columns={this.columns}
        />
        {!!this.state.selected.length && (
          <div className="d-flex align-items-center mt-3">
            <span className="pr-3 ml-4 text-orange">
              {this.props.t(["COUNT_RECORD_SELECTED", "COUNT_RECORD_SELECTED_plural", "count"], {
                count: this.state.selected.length,
              })}
            </span>
            <Button
              size="small"
              color="negative"
              onClick={this.onDeleteHierarchyShiftPatternAssignment(this.state.selected)}
            >
              {this.props.t("DELETE")}
            </Button>
            <Button size="small" color="secondary" onClick={this.deselectAllHandler}>
              {this.props.t("DESELECT_ALL")}
            </Button>
          </div>
        )}
      </Fragment>
    );
  }
}

export default localize()(ShiftPatternsTable);
